import { PLAYER_SKIN_KEYS } from '../../assets/asset-keys.js';
import { DIRECTION } from '../../common/direction.js';

// ── Overworld character skins (MPWSP "ow*" sheets). ──
// Each sheet is a 4col x 4row 32px grid. Rows are DOWN / LEFT / RIGHT / UP, columns are walk
// frames. The neutral standing frame of each direction is the first column (base = row * 4).
// The directional walk animations themselves (`SKIN_<key>_<DIR>`) are created once in
// preload-scene.js (#createSkinAnimations). This module centralizes the per-skin frame math so
// the Player, NPC and remote-player renderers all stay in sync with the sheet layout.

const ROW = Object.freeze({ DOWN: 0, LEFT: 1, RIGHT: 2, UP: 3 });

/** Idle (standing) frame index per facing direction for a skin sheet. */
export const SKIN_IDLE_FRAME_CONFIG = Object.freeze({
  [DIRECTION.DOWN]: ROW.DOWN * 4,
  [DIRECTION.LEFT]: ROW.LEFT * 4,
  [DIRECTION.RIGHT]: ROW.RIGHT * 4,
  [DIRECTION.UP]: ROW.UP * 4,
  [DIRECTION.NONE]: ROW.DOWN * 4,
});

/** The ordered list of selectable skin asset keys. */
export const SKIN_KEYS = PLAYER_SKIN_KEYS;

/** The number of selectable skins. */
export const SKIN_COUNT = PLAYER_SKIN_KEYS.length;

/** Default skin used when none has been chosen yet. */
export const DEFAULT_SKIN_KEY = PLAYER_SKIN_KEYS[0];

/**
 * Resolve a skin key from a stored value, falling back to the default when it is missing or
 * no longer valid (e.g. a save from before the skin list changed).
 * @param {string | undefined | null} value
 * @returns {string}
 */
export function resolveSkinKey(value) {
  return value && PLAYER_SKIN_KEYS.includes(value) ? value : DEFAULT_SKIN_KEY;
}

/**
 * Deterministically pick a skin for an NPC so each NPC id reads as a distinct character.
 * @param {number} npcId
 * @returns {string}
 */
export function skinForNpcId(npcId) {
  const i = Math.abs(Number(npcId) || 0) % PLAYER_SKIN_KEYS.length;
  return PLAYER_SKIN_KEYS[i];
}

/**
 * Standing frame index for a skin/direction. Used to seed the static frame and for the
 * remote-player renderer which does not run the grid-movement animations.
 * @param {import('../../common/direction.js').Direction} direction
 * @returns {number}
 */
export function skinIdleFrame(direction) {
  return SKIN_IDLE_FRAME_CONFIG[direction] ?? ROW.DOWN * 4;
}
